import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, AlertTriangle, CheckCircle2, Clock, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CAT_LABELS = {
  harcelement: "Harcèlement", violence: "Violence", discrimination: "Discrimination",
  corruption: "Corruption", mauvais_comportement: "Mauvais comportement", autre: "Autre",
};
const CAT_COLORS = {
  harcelement: "bg-red-100 text-red-800", violence: "bg-red-100 text-red-800",
  discrimination: "bg-orange-100 text-orange-800", corruption: "bg-purple-100 text-purple-800",
  mauvais_comportement: "bg-yellow-100 text-yellow-800", autre: "bg-slate-100 text-slate-800",
};
const STATUS_LABELS = {
  nouveau: { label: "Nouveau", color: "bg-blue-100 text-blue-800", icon: AlertTriangle },
  en_cours: { label: "En cours", color: "bg-orange-100 text-orange-800", icon: Clock },
  traite: { label: "Traité", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  clos: { label: "Clos", color: "bg-slate-200 text-slate-700", icon: XCircle },
};

export default function SignalementAdminPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ status: "", category: "" });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [newStatus, setNewStatus] = useState("en_cours");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.category) params.category = filter.category;
      const [r, s] = await Promise.all([
        api.get("/erp/signalements", { params }),
        api.get("/erp/signalements/stats"),
      ]);
      setRows(r.data);
      setStats(s.data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, [filter]);

  const openDetail = (r) => {
    setSelected(r);
    setNote("");
    setNewStatus(r.status);
  };

  const saveUpdate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/erp/signalements/${selected.id}`, { status: newStatus, admin_note: note || undefined });
      toast.success("Mis à jour");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="signalement-admin-page">
      <div>
        <div className="chip mb-2"><ShieldAlert className="h-3 w-3" /><span>SIGNALEMENTS CONFIDENTIELS</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Signalements anonymes</h1>
        <p className="text-slate-500 mt-1">Signalements reçus. L&apos;identité des expéditeurs n&apos;est jamais enregistrée.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-xs uppercase text-slate-500">Total</div>
            <div className="text-2xl font-extrabold text-[#1E3A8A]">{stats.total}</div>
          </div>
          {stats.by_status.map(s => (
            <div key={s.status} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-xs uppercase text-slate-500">{STATUS_LABELS[s.status]?.label || s.status}</div>
              <div className="text-2xl font-extrabold text-[#1E3A8A]">{s.count}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filter.status || "__all"} onValueChange={(v) => setFilter({ ...filter, status: v === "__all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Tous statuts</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.category || "__all"} onValueChange={(v) => setFilter({ ...filter, category: v === "__all" ? "" : v })}>
          <SelectTrigger className="w-56" data-testid="filter-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Toutes catégories</SelectItem>
            {Object.entries(CAT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Catégorie</th>
                <th className="px-4 py-3 text-left">Sujet</th>
                <th className="px-4 py-3 text-left">Créé le</th>
                <th className="px-4 py-3 text-center">Statut</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const s = STATUS_LABELS[r.status] || STATUS_LABELS.nouveau;
                const Ico = s.icon;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(r)} data-testid={`sig-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-[#1E3A8A]">{r.tracking_code}</td>
                    <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${CAT_COLORS[r.category]}`}>{CAT_LABELS[r.category]}</span></td>
                    <td className="px-4 py-2 max-w-md truncate">{r.subject}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-2 text-center"><span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${s.color}`}><Ico className="h-3 w-3" />{s.label}</span></td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Aucun signalement pour ce filtre.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.subject}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-xs">
                <span className={`px-2 py-0.5 rounded-full font-semibold ${CAT_COLORS[selected.category]}`}>{CAT_LABELS[selected.category]}</span>
                <span className="text-slate-500 font-mono">{selected.tracking_code}</span>
                <span className="text-slate-500">{new Date(selected.created_at).toLocaleString("fr-FR")}</span>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="text-xs text-slate-500 mb-1">Description</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{selected.description}</div>
              </div>
              {selected.location && (
                <div className="text-sm"><span className="text-slate-500">Lieu :</span> {selected.location}</div>
              )}
              {selected.reported_person && (
                <div className="text-sm"><span className="text-slate-500">Personne mise en cause :</span> {selected.reported_person}</div>
              )}
              {selected.admin_notes?.length > 0 && (
                <div className="border-t border-slate-100 pt-3">
                  <div className="text-xs uppercase text-slate-500 mb-2">Historique interne</div>
                  {selected.admin_notes.map((n, i) => (
                    <div key={i} className="bg-blue-50 rounded-lg p-2 mb-2 text-sm">
                      <div className="text-xs text-slate-500">{n.author_role} — {new Date(n.at).toLocaleDateString("fr-FR")}</div>
                      <div>{n.note}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t border-slate-100 pt-3 space-y-3">
                <div>
                  <label className="text-xs uppercase text-slate-500">Nouveau statut</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="new-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500">Note interne (optionnelle)</label>
                  <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Actions entreprises..." data-testid="admin-note" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
            <Button onClick={saveUpdate} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="sig-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mettre à jour"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
